"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import "swiper/swiper-bundle.css";
import { Autoplay } from "swiper/modules";
import { useState } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const LogoImage = ({ src }: { src: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  return (
    <div className="relative h-20 w-full">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className="h-5 w-5 animate-spin rounded-full border-b-2"
            style={{ borderColor: colors.primary }}
          />
        </div>
      )}
      <Image
        src={src}
        alt="Partner logo"
        fill
        className={`object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
      />
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: `${colors.primary}10` }}>
          <div className="text-2xl" style={{ color: colors.primary }}>?</div>
        </div>
      )}
    </div>
  );
};

const Carousel = () => {
  const banks = [
    "/images/brands/amharabank.jpeg",
    "/images/brands/awashbank.png",
    "/images/brands/cbe.jpeg",
    "/images/brands/ahadu12.png",
    "/images/brands/enatbank.png",
    "/images/brands/dev.png",
    "/images/brands/Boa.png",
  ];

  const insurance = [
    "/images/brands/abayinsu.jpeg",
    "/images/brands/lionin.jpeg",
    "/images/brands/lucyinsu.png",
    "/images/brands/NICE.png",
    "/images/brands/zemeninsurance.jpeg",
    "/images/brands/ethlife.png",
    "/images/brands/bunna.png",
  ];

  const institutions = [
    "/images/brands/AAWSA.png",
    "/images/brands/addissbaba.jpeg",
    "/images/brands/careethio.jpeg",
    "/images/brands/ethiotelecom.jpeg",
    "/images/brands/firstcon.jpg",
    "/images/brands/SNV.png",
    "/images/brands/rideh.png",
    "/images/brands/aregagach.png",
    "/images/brands/ethiopian_deposite.png",
    "/images/brands/partne1.jpg",
    "/images/brands/partner2.jpg",
    "/images/brands/partner3.jpg",
    "/images/brands/partner4.jpg",
    "/images/brands/partner5.jpg",
  ];

  const renderCategory = (title: string, logos: string[]) => (
    <div className="mb-16">
      <h3 
        className="mb-8 text-center text-2xl font-bold"
        style={{ color: colors.primary }}
      >
        {title}
      </h3>
      <div className="relative">
        <Swiper
          spaceBetween={30}
          slidesPerView={1}
          loop={true}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
          }}
          modules={[Autoplay]}
          breakpoints={{
            640: { slidesPerView: 3 },
            768: { slidesPerView: 4 },
            1024: { slidesPerView: 5 },
          }}
          className="partner-swiper"
        >
          {logos.map((logo, index) => (
            <SwiperSlide key={`${title}-${index}`}>
              <div 
                className="rounded-lg p-4 transition-all duration-300 hover:shadow-lg"
                style={{ 
                  backgroundColor: colors.white,
                  border: `1px solid ${colors.primary}15`,
                }}
              >
                <LogoImage src={logo} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );

  return (
    <section 
      className="px-4 py-16 sm:px-6 lg:px-8"
      style={{ backgroundColor: colors.whiteOff }}
    >
      <div className="max-w-8xl mx-auto">
        <div className="mb-14 text-center">
          <h2 className="text-4xl font-extrabold sm:text-5xl" style={{ color: colors.textDark }}>
            Our <span style={{ color: colors.primary }}>Trusted</span> Partners
          </h2>
          <div 
            className="mt-4 w-24 h-1 mx-auto rounded-full"
            style={{ backgroundColor: colors.primary }}
          />
        </div>

        {renderCategory("Banking Partners", banks)}
        {renderCategory("Insurance Partners", insurance)}
        {renderCategory("Institutional Partners", institutions)}
      </div>

      {/* Custom styles for Swiper navigation dots */}
      <style jsx global>{`
        .partner-swiper .swiper-pagination-bullet {
          background-color: ${colors.primary} !important;
        }
        .partner-swiper .swiper-pagination-bullet-active {
          background-color: ${colors.primaryDark} !important;
        }
        .partner-swiper .swiper-button-next,
        .partner-swiper .swiper-button-prev {
          color: ${colors.primary} !important;
        }
        .partner-swiper .swiper-button-next:hover,
        .partner-swiper .swiper-button-prev:hover {
          color: ${colors.primaryDark} !important;
        }
      `}</style>
    </section>
  );
};

export default Carousel;